import { app } from 'application';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { InstagramPostsModal, PageContainer, SearchInput, Pagination } from 'UI/elements';
import styles from './InstagramContentModerationPage.module.scss';
import { Creative } from 'domains/creative';
import { Dropdown, DropdownOption } from 'UI/elements/dropdown/Dropdown';

const HEADER_CELLS: { id: keyof Creative; name: string }[] = [
  {
    id: 'username',
    name: 'Username',
  },
  {
    id: 'totalPendingPost',
    name: 'Total Pending Posts',
  },
  {
    id: 'timestamp',
    name: 'Timestamp',
  },
  {
    id: 'category',
    name: 'Category',
  },
  {
    id: 'status',
    name: 'Status',
  },
];

function InstagramContentModerationPage(): React.ReactElement {
  const creatives = app().creativesStore.creatives;
  const filteredCreatives = app().creativesStore.filteredCreatives;
  const filterStringDatesDropdown: DropdownOption[] = app().creativesStore.filterStringDates.map(
    date => ({
      label: date,
      value: date,
    })
  );

  const [selectedFilterStringDate, setSelectedFilterStringDate] = useState<string>('');

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    app().creativesStore.getFirstPage();
  };

  const handleSelectUser = async (user: Creative) => {
    await app().instagramStore.getInstagramPostsByUserId({
      userId: user.id,
    });
    setIsModalOpen(true);
  };

  useEffect((): void => {
    const getFirstPage = async () => {
      await app().creativesStore.getFirstPage();
    };
    getFirstPage();
  }, []);

  const handleSearch = async (query: string) => {
    await app().creativesStore.search(query);
  };

  const handleFilterByDate = async (date: string) => {
    setSelectedFilterStringDate(date);
    await app().creativesStore.filterByDate(date);
  };

  // just a temporary solution to render the creatives
  const creativesToRender = filteredCreatives?.length ? filteredCreatives : creatives?.items;

  return (
    <PageContainer
      searchComponent={<SearchInput placeholder="Find a user" onSearch={handleSearch} />}
    >
      <div className={styles.wrapper}>
        <div className={styles.filterWrapper}>
          <Dropdown
            placeholder="Select Date"
            options={filterStringDatesDropdown}
            value={selectedFilterStringDate}
            onChange={handleFilterByDate}
          />
        </div>

        <div className={styles.categoriesTableWrapper}>
          <table className={styles.categoriesTable}>
            <thead>
              <tr>
                {HEADER_CELLS.map(cell => (
                  <th key={cell.id} className={styles.headerCell}>
                    {cell.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {creativesToRender?.map(creative => (
                <tr
                  onClick={() => handleSelectUser(creative)}
                  className={styles.tableRowButton}
                  key={creative.id}
                >
                  {HEADER_CELLS.map(cell => (
                    <td key={cell.id} className={styles.tableCell}>
                      <div className={styles.tableCellContent}>
                        {cell.id === 'username' ? (
                          creative.profileImage ? (
                            <img
                              src={creative.profileImage}
                              alt="creative"
                              className={styles.tableCellImage}
                            />
                          ) : (
                            <div className={styles.tableCellImagePlaceholder} />
                          )
                        ) : null}
                        {cell.id === 'category'
                          ? creative.category?.name
                          : creative[cell.id]?.toString()}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination below the table */}
        {!filteredCreatives?.length && creatives && (
          <div className={styles.paginationWrapper}>
            <Pagination
              currentPage={creatives.currentPage}
              totalPages={creatives.lastPage}
              onPageChange={async page => {
                if (page < 1 || page > creatives.lastPage || page === creatives.currentPage) return;
                if (page === 1) await app().creativesStore.getFirstPage();
                else if (page === creatives.lastPage) await app().creativesStore.goLastPage();
                else await app().creativesStore.getPage(page);
              }}
            />
          </div>
        )}
      </div>

      <InstagramPostsModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </PageContainer>
  );
}

const InstagramContentModerationPageWithObserver = observer(InstagramContentModerationPage);

export { InstagramContentModerationPageWithObserver as InstagramContentModerationPage };
